import { defaultServiceFormValues, serviceFormSchemaRefined, } from './service.schema';
import { translateApp } from "@/i18n";
const DEFAULT_STATE = 'ACT';
export function serviceToFormValues(service) {
    if (!service) {
        return cloneDefaultFormValues();
    }
    return {
        ...cloneDefaultFormValues(),
        name: service.name ?? '',
        description: service.description ?? null,
        duration: minutesToHours(service.duration),
        provider_id: normalizeId(service.provider?.id ?? service.provider_id),
        category_id: normalizeNullableId(service.category?.id ?? service.category_id),
        service_type_id: normalizeNullableId(service.service_type?.id ?? service.service_type_id),
        country: service.country ?? '',
        state: service.state ?? DEFAULT_STATE,
        location_one: service.location_one ?? null,
        location_two: service.location_two ?? null,
        min_time_booking: normalizeInteger(service.min_time_booking, 1),
        max_time_booking: normalizeInteger(service.max_time_booking, 364),
        cancellation_policy_duration: normalizeInteger(service.cancellation_policy_duration, 0),
        photo_storage_ids: normalizePhotoStorageIds(service.photo_storage_ids),
        additional_information: normalizeOptionalText(service.additional_information),
    };
}
export function formValuesToCreatePayload(values, photoStorageIds = values.photo_storage_ids ?? []) {
    const normalized = normalizeFormValues(values, photoStorageIds);
    return {
        ...normalized,
        duration: hoursToMinutes(normalized.duration),
    };
}
export function formValuesToUpdatePayload(values, photoStorageIds = values.photo_storage_ids ?? []) {
    return formValuesToCreatePayload(values, photoStorageIds);
}
export function validateServiceFormValues(values) {
    const parsed = serviceFormSchemaRefined.safeParse(normalizeFormValues(values, values.photo_storage_ids ?? []));
    if (parsed.success) {
        return [];
    }
    return parsed.error.issues.map((issue) => ({
        [String(issue.path[0] ?? 'form')]: issue.message,
    }));
}
export function normalizeApiErrors(error) {
    const responseMessage = error?.response?.data?.message;
    if (Array.isArray(responseMessage)) {
        return responseMessage.flatMap((item) => normalizeApiErrorItem(item));
    }
    if (typeof responseMessage === 'string') {
        return [{ form: responseMessage }];
    }
    if (error instanceof Error) {
        return [{ form: error.message }];
    }
    return [{ form: translateApp("services.validation.save_error") }];
}
/**
 * Handles two message-item shapes the backend can send: a plain `{ field: message }`
 * pair, or a raw class-validator error (`{ property, constraints, target, value }`)
 * from Nest's ValidationPipe — the latter must be reduced to a human message, or it
 * leaks as a JSON blob (target/value/constraints) straight into the toast/field error.
 */
function normalizeApiErrorItem(item) {
    if (typeof item === 'string') {
        return [{ form: item }];
    }
    if (!item || typeof item !== 'object') {
        return [];
    }
    const record = item;
    const constraintsMessage = extractConstraintsMessage(record.constraints);
    if (constraintsMessage) {
        const field = typeof record.property === 'string' ? record.property : 'form';
        return [{ [field]: constraintsMessage }];
    }
    const [field, value] = Object.entries(record)[0] ?? [];
    if (field && typeof value === 'string') {
        return [{ [field]: value }];
    }
    return [{ form: translateApp("services.validation.save_error") }];
}
function extractConstraintsMessage(constraints) {
    if (Array.isArray(constraints)) {
        const message = constraints.filter((c) => typeof c === 'string').join('. ');
        return message || null;
    }
    if (constraints && typeof constraints === 'object') {
        const message = Object.values(constraints)
            .filter((c) => typeof c === 'string')
            .join('. ');
        return message || null;
    }
    return null;
}
export function cloneDefaultFormValues() {
    return {
        ...defaultServiceFormValues,
        photo_storage_ids: [...defaultServiceFormValues.photo_storage_ids],
    };
}
export function normalizePhotoStorageIds(ids) {
    if (!Array.isArray(ids)) {
        return [];
    }
    return [...new Set(ids.map((id) => Number(id)).filter((id) => Number.isInteger(id) && id > 0))];
}
export function hoursToMinutes(value) {
    const hours = Number(value);
    if (!Number.isFinite(hours) || hours <= 0) {
        return 0;
    }
    return Math.round(hours * 60);
}
export function minutesToHours(value) {
    const minutes = Number(value);
    if (!Number.isFinite(minutes) || minutes <= 0) {
        return 1;
    }
    return Number((minutes / 60).toFixed(2));
}
function normalizeFormValues(values, photoStorageIds = []) {
    const base = cloneDefaultFormValues();
    return {
        ...base,
        ...values,
        name: normalizeRequiredText(values.name),
        description: normalizeOptionalText(values.description),
        duration: normalizeNumber(values.duration, base.duration),
        provider_id: normalizeId(values.provider_id),
        category_id: normalizeNullableId(values.category_id),
        service_type_id: normalizeNullableId(values.service_type_id),
        country: normalizeRequiredText(values.country),
        state: normalizeRequiredText(values.state) || DEFAULT_STATE,
        location_one: normalizeOptionalText(values.location_one),
        location_two: normalizeOptionalText(values.location_two),
        min_time_booking: normalizeInteger(values.min_time_booking, base.min_time_booking),
        max_time_booking: normalizeInteger(values.max_time_booking, base.max_time_booking),
        cancellation_policy_duration: normalizeInteger(values.cancellation_policy_duration, base.cancellation_policy_duration),
        photo_storage_ids: normalizePhotoStorageIds(photoStorageIds),
        additional_information: normalizeOptionalText(values.additional_information),
    };
}
function normalizeId(value) {
    return normalizeInteger(value, 0);
}
function normalizeNullableId(value) {
    const id = normalizeInteger(value, 0);
    return id > 0 ? id : null;
}
function normalizeInteger(value, fallback) {
    if (value === null || value === undefined || value === '') {
        return fallback;
    }
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
        return fallback;
    }
    return Math.trunc(parsed);
}
function normalizeNumber(value, fallback) {
    if (value === null || value === undefined || value === '') {
        return fallback;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
}
function normalizeRequiredText(value) {
    return typeof value === 'string' ? value.trim() : '';
}
function normalizeOptionalText(value) {
    if (typeof value !== 'string') {
        return value == null ? null : String(value);
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
}
